import { createContext, useContext, useLayoutEffect } from 'react';

import { theme as antdTheme } from 'antd';
import { darkTheme, lightTheme } from 'src/theme';
import { useLocalStorage } from 'usehooks-ts';

import type { ThemeContextType } from './ThemeContext.types';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useLocalStorage('isDarkMode', false);
  const { token } = antdTheme.useToken();

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => !prevMode);
  };

  const themeAlgorithm = isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;
  const customTheme = isDarkMode ? darkTheme : lightTheme;
  const combinedTheme = { ...token, ...customTheme.token };
  const setIsDarkTheme = (isDarkTheme: boolean) => {
    setIsDarkMode(isDarkTheme);
  };

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-prefers-color-scheme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        toggleTheme,
        themeAlgorithm,
        customTheme,
        combinedTheme,
        setIsDarkTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
